import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CitySelect } from "@/components/Selects/CitySelect";
import type { CityOption } from "@/components/Selects/CitySelect";
import { SpecialtiesSelect } from "@/components/Selects/SpecialtiesSelect";
import { USStateSelect } from "@/components/Selects/USStateSelect";
import { HealthcareProfessionalTypeSelect } from "@/components/Selects/HealthcareProfessionalTypeSelect";
import { EmploymentTypeSelect } from "@/components/Selects/EmploymentTypeSelect";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc } from "@/trpc";
import { EmploymentType } from "@api/shared-runtime/enums";
import { ProfileModal } from "@/features/profile/components/ProfileModal";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AdminUserDetails } from "@/features/user/components/AdminUserDetails";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, MessageSquare } from "lucide-react";
import { ChatThreadBody } from "@/features/chat/chat-thread-body";
import { ChatThreadMessage } from "@/features/chat/types/chat-thread-message";
import { adaptAdminMessageToUserMessage } from "@/features/chat/admin/adaptAdminMessageToUserMessage";
import { toast } from "sonner";

const searchFormSchema = z.object({
  specialties: z.array(z.string()).default([]),
  professionalTypes: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    )
    .default([]),
  city: z.array(
    z.object({
      name: z.string(),
      lat: z.number().optional(),
      lng: z.number().optional(),
    }),
  ),
  distance: z.string().min(1, "Please select a distance"),
  employmentTypeIds: z.array(z.nativeEnum(EmploymentType)).optional(),
  stateLicenses: z.array(z.string()),
  hasImlcLicense: z.string(), // "true", "false", or "any"
});

type SearchFormData = z.infer<typeof searchFormSchema>;
type ParsedProfessionalType = { id: string; name: string };
type StateMedicalLicense = {
  state: string;
  license_number?: string;
  expiration_date?: string | null;
};

const isParsedProfessionalType = (
  value: unknown,
): value is ParsedProfessionalType =>
  typeof value === "object" &&
  value !== null &&
  "id" in value &&
  "name" in value &&
  typeof value.id === "string" &&
  typeof value.name === "string";

const isStateMedicalLicense = (value: unknown): value is StateMedicalLicense =>
  typeof value === "object" &&
  value !== null &&
  "state" in value &&
  typeof value.state === "string" &&
  value.state.length > 0;

function ProviderSearch() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const parsedSpecialties =
    search.specialties?.split(",").filter(Boolean) ??
    (search.specialty ? [search.specialty] : []);

  let parsedProfessionalTypes: { id: string; name: string }[] = [];
  if (search.professionalTypes) {
    try {
      const values = JSON.parse(search.professionalTypes);
      if (Array.isArray(values)) {
        parsedProfessionalTypes = values.filter(isParsedProfessionalType);
      }
    } catch {
      parsedProfessionalTypes = [];
    }
  } else if (search.professionalTypeId) {
    parsedProfessionalTypes = [
      {
        id: search.professionalTypeId,
        name: search.professionalTypeName || "",
      },
    ];
  }
  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<SearchFormData>({
    resolver: zodResolver(searchFormSchema),
    defaultValues: {
      specialties: parsedSpecialties,
      professionalTypes: parsedProfessionalTypes,
      city: search.cityName
        ? [
            {
              name: search.cityName || "",
              lat: Number(search.lat) || undefined,
              lng: Number(search.lng) || undefined,
            },
          ]
        : [],
      distance: search.distance || "100",
      employmentTypeIds: search.employmentTypeIds
        ? (search.employmentTypeIds.split(",") as EmploymentType[])
        : undefined,
      stateLicenses: search.stateLicenses
        ? search.stateLicenses.split(",")
        : [],
      hasImlcLicense: search.hasImlcLicense || "any",
    },
  });

  const [searchParams, setSearchParams] = useState<{
    lat?: number;
    lng?: number;
    specialtyCodes?: string[];
    distanceMiles?: number;
    employmentTypeIds?: EmploymentType[];
    stateLicenses?: string[];
    hasImlcLicense?: boolean;
    healthcareProfessionalTypeIds?: string[];
  } | null>(() => {
    const lat = search.lat ? Number(search.lat) : undefined;
    const lng = search.lng ? Number(search.lng) : undefined;
    const distanceMiles = search.distance ? Number(search.distance) : undefined;
    const specialtyCodes =
      parsedSpecialties && parsedSpecialties.length > 0
        ? parsedSpecialties
        : undefined;
    const employmentTypeIds = search.employmentTypeIds
      ? (search.employmentTypeIds.split(",") as EmploymentType[])
      : undefined;
    const parsedStateLicenses = search.stateLicenses
      ? search.stateLicenses.split(",").filter(Boolean)
      : undefined;
    const stateLicenses =
      parsedStateLicenses && parsedStateLicenses.length > 0
        ? parsedStateLicenses
        : undefined;
    const hasImlcLicense =
      search.hasImlcLicense === "true"
        ? true
        : search.hasImlcLicense === "false"
          ? false
          : undefined;
    const healthcareProfessionalTypeIds =
      parsedProfessionalTypes.length > 0
        ? parsedProfessionalTypes.map((type) => type.id)
        : undefined;

    const hasFilters =
      (specialtyCodes && specialtyCodes.length > 0) ||
      (lat !== undefined && lng !== undefined) ||
      (stateLicenses && stateLicenses.length > 0) ||
      typeof hasImlcLicense === "boolean" ||
      (healthcareProfessionalTypeIds &&
        healthcareProfessionalTypeIds.length > 0);

    if (!hasFilters) {
      return null;
    }

    return {
      lat,
      lng,
      specialtyCodes,
      distanceMiles,
      employmentTypeIds,
      stateLicenses,
      hasImlcLicense,
      healthcareProfessionalTypeIds,
    };
  });

  const hasSearchFilters =
    !!searchParams &&
    ((searchParams.specialtyCodes && searchParams.specialtyCodes.length > 0) ||
      (searchParams.stateLicenses && searchParams.stateLicenses.length > 0) ||
      typeof searchParams.hasImlcLicense === "boolean" ||
      (searchParams.lat !== undefined && searchParams.lng !== undefined) ||
      (searchParams.healthcareProfessionalTypeIds &&
        searchParams.healthcareProfessionalTypeIds.length > 0));
  const [profileModalUserId, setProfileModalUserId] = useState<string | null>(
    null,
  );
  const [userDetailsUserId, setUserDetailsUserId] = useState<string | null>(
    null,
  );
  const [chatDialogUser, setChatDialogUser] = useState<{
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null>(null);
  const [chatDialogThreadId, setChatDialogThreadId] = useState<string | null>(
    null,
  );
  const [isChatDialogOpen, setIsChatDialogOpen] = useState(false);
  const [chatLoadingUserId, setChatLoadingUserId] = useState<string | null>(
    null,
  );

  const ensureChatMutation = useMutation(
    trpc.jobPost.adminEnsurePotentialCandidateChat.mutationOptions({
      onError: () => {
        toast.error("Unable to open chat. Please try again.");
      },
    }),
  );

  const handleOpenChat = async (provider: {
    user_id: string;
    first_name: string | null;
    last_name: string | null;
  }) => {
    setChatLoadingUserId(provider.user_id);
    try {
      const result = await ensureChatMutation.mutateAsync({
        userId: provider.user_id,
      });
      setChatDialogUser({
        id: provider.user_id,
        firstName: provider.first_name,
        lastName: provider.last_name,
      });
      setChatDialogThreadId(result.threadId);
      setIsChatDialogOpen(true);
    } catch {
      toast.error("Unable to open chat. Please try again.");
    } finally {
      setChatLoadingUserId(null);
    }
  };

  const handleChatDialogChange = (open: boolean) => {
    if (!open) {
      setIsChatDialogOpen(false);
      setChatDialogThreadId(null);
      setChatDialogUser(null);
    } else {
      setIsChatDialogOpen(true);
    }
  };

  const { data: providers = [], isLoading } = useQuery({
    ...trpc.organization.adminSearchProviders.queryOptions(searchParams ?? {}),
    enabled: hasSearchFilters,
  });

  const distanceOptions = [
    { value: "50", label: "50 miles" },
    { value: "100", label: "100 miles" },
    { value: "300", label: "300 miles" },
  ];

  const onSubmit = (data: SearchFormData) => {
    const city = data.city[0];
    const lat = city?.lat;
    const lng = city?.lng;

    const specialtyCodes = data.specialties ?? [];
    const professionalTypeSelections = data.professionalTypes ?? [];
    const professionalTypeIds = professionalTypeSelections.map(
      (type) => type.id,
    );
    const hasLocation = lat !== undefined && lng !== undefined;
    const hasStateLicenses = data.stateLicenses.length > 0;
    const stateLicenses = hasStateLicenses ? data.stateLicenses : undefined;
    const hasImlcLicenseValue =
      data.hasImlcLicense === "true"
        ? true
        : data.hasImlcLicense === "false"
          ? false
          : undefined;
    const employmentTypeIds =
      data.employmentTypeIds && data.employmentTypeIds.length > 0
        ? data.employmentTypeIds
        : undefined;

    const params = {
      lat,
      lng,
      specialtyCodes: specialtyCodes.length > 0 ? specialtyCodes : undefined,
      distanceMiles: parseInt(data.distance),
      employmentTypeIds,
      stateLicenses,
      hasImlcLicense: hasImlcLicenseValue,
      healthcareProfessionalTypeIds:
        professionalTypeIds.length > 0 ? professionalTypeIds : undefined,
    };

    const hasFilters =
      specialtyCodes.length > 0 ||
      hasLocation ||
      hasStateLicenses ||
      typeof hasImlcLicenseValue === "boolean" ||
      professionalTypeIds.length > 0;

    setSearchParams(hasFilters ? params : null);

    const urlSearchParams: Record<string, string> = {
      distance: data.distance,
    };

    if (lat && lng && city) {
      urlSearchParams.lat = String(lat);
      urlSearchParams.lng = String(lng);
      urlSearchParams.cityName = city.name;
    }

    if (specialtyCodes.length > 0) {
      urlSearchParams.specialties = specialtyCodes.join(",");
    }

    if (hasStateLicenses) {
      urlSearchParams.stateLicenses = data.stateLicenses.join(",");
    }

    if (data.hasImlcLicense !== "any") {
      urlSearchParams.hasImlcLicense = data.hasImlcLicense;
    }

    if (professionalTypeSelections.length > 0) {
      urlSearchParams.professionalTypes = JSON.stringify(
        professionalTypeSelections,
      );
    }

    if (employmentTypeIds && employmentTypeIds.length > 0) {
      urlSearchParams.employmentTypeIds = employmentTypeIds.join(",");
    }

    navigate({
      // @ts-expect-error - existing dynamic route typing
      search: urlSearchParams,
    });
  };

  return (
    <>
      <div className="container mx-auto py-8 px-4">
        <Card className="p-6">
          <h1 className="text-2xl font-bold mb-6">Find Healthcare Providers</h1>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="md:col-span-4">
                <label className="block text-sm font-medium mb-2">
                  Professional Type
                </label>
                <HealthcareProfessionalTypeSelect
                  isMulti
                  value={watch("professionalTypes")}
                  onChange={(value) =>
                    setValue(
                      "professionalTypes",
                      (value as { id: string; name: string }[] | undefined) ??
                        [],
                      {
                        shouldValidate: true,
                      },
                    )
                  }
                  placeholder="Select professional type"
                />
              </div>

              <div className="md:col-span-4">
                <label className="block text-sm font-medium mb-2">
                  Specialties
                </label>
                <SpecialtiesSelect
                  isMulti
                  value={watch("specialties")}
                  onChange={(value) =>
                    setValue("specialties", (value as string[] | null) ?? [], {
                      shouldValidate: true,
                    })
                  }
                />
                {errors.specialties && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.specialties.message}
                  </p>
                )}
              </div>

              <div className="md:col-span-4">
                <label className="block text-sm font-medium mb-2">City</label>
                <CitySelect
                  value={watch("city")}
                  onChange={(value: CityOption[]) =>
                    setValue("city", value, { shouldValidate: true })
                  }
                />
                {errors.city && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.city.message}
                  </p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">
                  Employment Type
                </label>
                <EmploymentTypeSelect
                  value={watch("employmentTypeIds")}
                  onChange={(value) => setValue("employmentTypeIds", value)}
                  placeholder="Any type"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">
                  Distance
                </label>
                <Select
                  value={watch("distance")}
                  onValueChange={(value) =>
                    setValue("distance", value, { shouldValidate: true })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select distance" />
                  </SelectTrigger>
                  <SelectContent>
                    {distanceOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.distance && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.distance.message}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  State Licenses
                </label>
                <USStateSelect
                  multiple={true}
                  value={watch("stateLicenses")}
                  onChange={(value) =>
                    setValue("stateLicenses", value as string[], {
                      shouldValidate: true,
                    })
                  }
                  placeholder="Select states"
                />
                {errors.stateLicenses && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.stateLicenses.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  IMLC License
                </label>
                <Select
                  value={watch("hasImlcLicense")}
                  onValueChange={(value) => setValue("hasImlcLicense", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button type="submit" className="w-full py-8">
              Search Providers
            </Button>
          </form>

          <div className="mt-8">
            {isLoading ? (
              <div className="text-center py-8">Loading...</div>
            ) : !searchParams ? (
              <div className="text-center py-8">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Enter Search Parameters
                </h3>
                <p className="text-gray-500">
                  Add filters above (specialty, city, state licenses, etc.) to
                  find providers
                </p>
              </div>
            ) : providers.length > 0 ? (
              <div className="space-y-4">
                {providers.map((provider) => (
                  <Card key={provider.user_id} className="p-4">
                    <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                      <div>
                        <h3 className="text-lg font-semibold">
                          {provider.first_name} {provider.last_name}
                        </h3>
                        <p>{provider.specialty_name}</p>
                        {provider.organizations &&
                          provider.organizations.length > 0 && (
                            <p className="text-sm">
                              <span className="text-gray-500">Orgs:</span>{" "}
                              {provider.organizations.map((org, i, arr) => (
                                <span key={i}>
                                  {org}
                                  {i < arr.length - 1 && " • "}
                                </span>
                              ))}
                            </p>
                          )}
                        <p className="text-sm">
                          <span className="text-gray-500">
                            Preferred locations:
                          </span>{" "}
                          {provider.locations
                            .filter((l) => l.name !== "ANY_CITY")
                            .map((l, i, arr) => (
                              <span key={l.name}>
                                {l.name}
                                {i < arr.length - 1 && " • "}
                              </span>
                            ))}
                          {provider.locations.some(
                            (l) => l.name === "ANY_CITY",
                          ) && (
                            <span className="text-gray-500">
                              {" "}
                              (open to other cities)
                            </span>
                          )}
                        </p>

                        <div className="flex flex-col gap-1">
                          <p className="text-sm">
                            <span className="text-gray-500">
                              Employment types:
                            </span>{" "}
                            <Popover>
                              <PopoverTrigger>
                                <span className="font-bold underline decoration-dotted cursor-help">
                                  {provider.employment_types.length === 0
                                    ? "Any Type"
                                    : provider.employment_types.join(" • ")}
                                </span>
                              </PopoverTrigger>
                              <PopoverContent className="w-64 rounded-md bg-white p-4 shadow-md border border-gray-200">
                                <div className="flex gap-2 items-center">
                                  <p className="text-sm text-gray-600">
                                    This provider's availability preference may
                                    change. Please confirm during outreach.
                                  </p>
                                </div>
                              </PopoverContent>
                            </Popover>
                          </p>
                          <p className="text-sm">
                            <span className="text-gray-500">
                              Practice settings:
                            </span>{" "}
                            {provider.practice_settings.length === 0
                              ? "Any Setting"
                              : provider.practice_settings.join(" • ")}
                          </p>
                          {provider.availability_description && (
                            <p className="text-sm">
                              <span className="text-gray-500">
                                Availability:
                              </span>{" "}
                              {provider.availability_description}
                            </p>
                          )}
                          <p className="text-sm">
                            <span className="text-gray-500">Min Wage:</span>{" "}
                            {!provider.minimum_rate ||
                            provider.minimum_rate === 0
                              ? "No minimum"
                              : `$${provider.minimum_rate}/hr`}
                          </p>
                          <p className="text-sm">
                            <span className="text-gray-500">IMLC License:</span>{" "}
                            <span className="font-semibold">
                              {provider.has_imlc_license ? "Yes" : "No"}
                            </span>
                          </p>
                          <div className="text-sm">
                            <span className="text-gray-500">
                              State Medical Licenses:
                            </span>{" "}
                            {Array.isArray(
                              (provider as { state_medical_licenses?: unknown })
                                .state_medical_licenses,
                            ) &&
                            (provider as { state_medical_licenses: unknown[] })
                              .state_medical_licenses.length > 0 ? (
                              <span className="inline-flex flex-wrap gap-1 ml-1">
                                {(
                                  provider as {
                                    state_medical_licenses: unknown[];
                                  }
                                ).state_medical_licenses
                                  .filter(isStateMedicalLicense)
                                  .map((license, index) => (
                                    <span
                                      key={index}
                                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                    >
                                      {license.state}
                                    </span>
                                  ))}
                              </span>
                            ) : (
                              <span className="text-gray-400">
                                No state licenses
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                        <Button
                          className="w-full md:w-auto"
                          onClick={() =>
                            setProfileModalUserId(provider.user_id)
                          }
                        >
                          View Provider Profile
                        </Button>
                        <Button
                          variant="secondary"
                          className="w-full md:w-auto"
                          onClick={() => setUserDetailsUserId(provider.user_id)}
                        >
                          User Details
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full md:w-auto"
                          onClick={() => handleOpenChat(provider)}
                          disabled={chatLoadingUserId === provider.user_id}
                        >
                          {chatLoadingUserId === provider.user_id ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <MessageSquare className="mr-2 h-4 w-4" />
                          )}
                          Chat
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  No providers found
                </h3>
                <p className="text-gray-500 mb-4">
                  Try adjusting your search criteria:
                </p>
                <ul className="text-gray-600 space-y-2">
                  <li>• Expand your search radius</li>
                  <li>• Try different specialties</li>
                  <li>• Search in nearby cities</li>
                </ul>
              </div>
            )}
          </div>
        </Card>
      </div>

      {profileModalUserId && (
        <ProfileModal
          userId={profileModalUserId}
          onClose={() => setProfileModalUserId(null)}
          showChatButton={false}
        />
      )}
      {userDetailsUserId && (
        <Dialog
          open={!!userDetailsUserId}
          onOpenChange={() => setUserDetailsUserId(null)}
        >
          <DialogContent className="max-w-5xl w-full">
            <DialogTitle className="sr-only">User Details</DialogTitle>
            <AdminUserDetails
              userId={userDetailsUserId}
              context="dialog"
              onClose={() => setUserDetailsUserId(null)}
            />
          </DialogContent>
        </Dialog>
      )}
      {chatDialogUser && (
        <ProviderChatDialog
          open={isChatDialogOpen}
          onOpenChange={handleChatDialogChange}
          user={chatDialogUser}
          threadId={chatDialogThreadId}
        />
      )}
    </>
  );
}

const ProviderChatDialog = ({
  open,
  onOpenChange,
  user,
  threadId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: { id: string; firstName: string | null; lastName: string | null };
  threadId: string | null;
}) => {
  const queryClient = useQueryClient();
  const {
    data: messages,
    isLoading,
    error,
    refetch,
  } = useQuery(
    trpc.chat.adminGetChatThreadMessages.queryOptions(
      { threadId: threadId ?? "", includeDeletedAttachments: false },
      {
        enabled: open && Boolean(threadId),
        staleTime: 0,
        refetchInterval: () => 5_000 + Math.random() * 5_000,
      },
    ),
  );

  const sendMessageMutation = useMutation(
    trpc.chat.sendMessage.mutationOptions({
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          queryKey: trpc.chat.adminGetChatThreadMessages.queryKey({
            threadId: variables.threadId,
            includeDeletedAttachments: false,
          }),
        });
      },
      onError: () => {
        toast.error("Unable to send message. Please try again.");
      },
    }),
  );

  const typedMessages = useMemo<ChatThreadMessage[] | undefined>(() => {
    if (!messages) {
      return undefined;
    }
    return messages.map(adaptAdminMessageToUserMessage);
  }, [messages]);

  const handleSendMessage = async (
    message: string,
    attachmentIds?: string[],
  ) => {
    if (!threadId) {
      toast.error("Chat is not ready yet. Please try again.");
      return;
    }
    await sendMessageMutation.mutateAsync({
      threadId,
      message,
      attachmentIds,
    });
  };

  if (!user) {
    return null;
  }

  const displayName =
    [user.firstName, user.lastName]
      .filter((part) => part && part.length > 0)
      .join(" ")
      .trim() || "Provider";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg md:max-w-xl lg:max-w-2xl h-[85vh] max-h-dvh w-lg flex flex-col p-0 shadow-2xl rounded-lg">
        <DialogHeader className="border-b p-4">
          <DialogTitle className="text-lg font-medium">
            {displayName}
          </DialogTitle>
          <DialogDescription>
            Chat directly with this provider.
          </DialogDescription>
        </DialogHeader>
        {!threadId || isLoading ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="flex flex-col items-center text-center">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-3" />
              <p className="text-gray-600">Loading conversation...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <Alert variant="destructive" className="max-w-md">
              <AlertDescription>
                There was an error loading this conversation. Please try again.
              </AlertDescription>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => refetch()}
              >
                Retry
              </Button>
            </Alert>
          </div>
        ) : (
          <ChatThreadBody
            messages={typedMessages ?? []}
            onSendMessage={handleSendMessage}
            readOnly={false}
            threadId={threadId ?? ""}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export const Route = createFileRoute("/_admin/admin/provider-search/")({
  component: ProviderSearch,
  validateSearch: (search: Record<string, string>) => ({
    lat: search.lat,
    lng: search.lng,
    cityName: search.cityName,
    specialty: search.specialty,
    specialties: search.specialties,
    distance: search.distance,
    employmentTypeIds: search.employmentTypeIds,
    stateLicenses: search.stateLicenses,
    hasImlcLicense: search.hasImlcLicense,
    professionalTypeId: search.professionalTypeId,
    professionalTypeName: search.professionalTypeName,
    professionalTypes: search.professionalTypes,
  }),
});
